import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { setTentativeETALocation } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { Label } from '../AirAmbulanceFormStandalone.styles';
import { PICKUP_LOCATION } from '../constants';
import {
  SuggestionsContainer,
  SuggestionsList,
} from './AddressSearchInput.styled';
import { SuggestedAddress } from './SuggestedAddress';

const AddressSearchInput = ({
  value,
  onChange,
  setFieldValue,
  onBlur,
  error,
  geocodedResults,
  onSelectSuggestion,
  onAddressChange,
  requestId,
}) => {
  const dispatch = useDispatch();
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // Handle selecting a suggestion
  const handleSelectSuggestion = suggestion => {
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    const attributes = suggestion?.attributes;
    const location = suggestion?.location;
    dispatch(
      setTentativeETALocation({
        latitude: location?.y,
        longitude: location?.x,
      }),
    );
  };

  // Handle address input change
  const handleChange = e => {
    onChange(e);
    onAddressChange(e);

    if (e.target.value) {
      setShowSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle click outside suggestions
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      dispatch(setTentativeETALocation(null));
    };
  }, []); //eslint-disable-line

  return (
    <Box pb={3}>
      <Label>
        <Text size="small" color="primaryText">
          Pickup Location*
        </Text>
      </Label>
      <SuggestionsContainer ref={suggestionsRef}>
        <InputText
          id={PICKUP_LOCATION}
          name={PICKUP_LOCATION}
          shouldErrorTakeSpace
          type="text"
          setFieldValue={setFieldValue}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          error={error}
          style={{ fontSize: '14px' }}
        />
        {showSuggestions && geocodedResults?.length > 0 && (
          <SuggestionsList>
            {geocodedResults.map((result, index) => (
              <SuggestedAddress
                key={index}
                isSelected={index === selectedSuggestionIndex}
                index={index}
                address={result}
                handleSelectSuggestion={handleSelectSuggestion}
                setSelectedSuggestionIndex={setSelectedSuggestionIndex}
              />
            ))}
          </SuggestionsList>
        )}
      </SuggestionsContainer>
    </Box>
  );
};

AddressSearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.string,
  geocodedResults: PropTypes.array,
  onSelectSuggestion: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  requestId: PropTypes.string,
};

export default AddressSearchInput;
